import styled from "styled-components";

import { imgList } from "@constants/imgList";
import { device } from "@constants/styles";

// Shared device-status icon renderers + the `Mode` icon tile.
// These 7 helpers are pure (only depend on imgList + Mode) and are byte-identical
// across Device.tsx, DeviceDetail2.tsx and BoxDevice — extracted here so all three
// consume a single source. `renderStatus` / `renderSound` are intentionally NOT
// here: they diverge per page by CSS class, so each page keeps its own variant.

interface IStyle {
  active: boolean;
  isError?: boolean;
}

export const Mode = styled.div<IStyle>`
  & .animate-flicker {
    -webkit-animation: flickerAnimation 2s infinite;
    animation: flickerAnimation 2s infinite;
  }
  position: relative;
  background: ${(props) =>
    props.active
      ? props.isError
        ? "rgba(255, 221, 221, 0.3)"
        : "#eef6ff"
      : "rgba(208, 209, 211, 0.4)"};
  border-radius: 0.417vw;
  > img {
    position: absolute;
    width: 58%;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
  @media ${device.desktopL} {
    border-radius: 6px;
  }
`;

export const renderWifi = (
  wifi = false,
  rssi: number | null = null,
  wifiStrength = ""
) => {
  // A missing rssi used to be defaulted to 0 at every call site, and 0 dBm
  // falls into the strongest bucket below — so "no reading" drew FULL bars.
  // Unknown gets the no-signal glyph instead: the conservative reading invites
  // a look, the flattering one hides a problem.
  if (wifiStrength === "offline" || rssi === null) {
    return (
      <Mode active={wifi}>
        <img src={imgList[`ico_wifi_offline`]} alt="" />
      </Mode>
    );
  } else {
    if (rssi > -50) {
      return (
        <Mode active={true}>
          <img src={imgList[`ico_wifi_active`]} alt="" />
        </Mode>
      );
    } else if (rssi <= -50 && rssi >= -59) {
      return (
        <Mode active={true}>
          <img src={imgList[`ico_wifi_1`]} alt="" />
        </Mode>
      );
    } else if (rssi <= -60 && rssi >= -69) {
      return (
        <Mode active={true} isError={true}>
          <img src={imgList[`ico_wifi_2`]} alt="" />
        </Mode>
      );
    } else {
      return (
        <Mode active={true} isError={true}>
          <img src={imgList[`ico_wifi_3`]} alt="" />
        </Mode>
      );
    }
  }
};

export const renderLight = (light = false) => {
  return (
    <Mode active={light}>
      <img
        src={imgList[`ico_light_bulb_${light ? "active" : "disable"}`]}
        alt=""
      />
    </Mode>
  );
};

export const renderComp = (status = "", comp = false, flash = false) => {
  const active = status !== "updating" && (comp || flash) ? true : false;
  return (
    <Mode active={active}>
      <img
        src={imgList[`ico_snow_${active ? "active" : "disable"}`]}
        alt=""
        className={`${flash ? "animate-flicker" : ""}`}
      />
    </Mode>
  );
};

export const renderFan = (status = "", fan = false, flash = false) => {
  const active = status !== "updating" && (fan || flash) ? true : false;
  return (
    <Mode active={active}>
      <img
        src={imgList[`ico_fan_${active ? "active" : "disable"}`]}
        alt=""
        className={`${flash ? "animate-flicker" : ""}`}
      />
    </Mode>
  );
};

export const renderDefrost = (status = "", defrost = false, flash = false) => {
  const active = status !== "updating" && (defrost || flash) ? true : false;
  return (
    <Mode active={active}>
      <img
        src={imgList[`ico_bulb_${active ? "active" : "disable"}`]}
        alt=""
        className={`${flash ? "animate-flicker" : ""}`}
      />
    </Mode>
  );
};

export const renderAlarm = (alarm = false, flash = false) => {
  return (
    <Mode active={alarm || flash} isError={false}>
      <img
        src={imgList[`ico_shield_${alarm || flash ? "active" : "disable"}`]}
        alt=""
        className={`${alarm || flash ? "animate-flicker" : ""}`}
      />
    </Mode>
  );
};

export const renderDoor = (status = "", door = false, flash = false) => {
  const active = status !== "updating" && (door || flash) ? true : false;
  return (
    <Mode active={active}>
      <img
        src={imgList[`ico_door_${active ? "active" : "disable"}`]}
        alt=""
        className={`${flash ? "animate-flicker" : ""}`}
      />
    </Mode>
  );
};
